import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useUi } from '@/store/ui';
import { useOpenDm, useSearch } from '@/hooks/queries';
import { Avatar, Spinner } from '@/components/ui';
import {
  ChannelIcon,
  ChatIcon,
  FilesIcon,
  MeetingsIcon,
  PeopleIcon,
  ProjectsIcon,
  SearchIcon,
  SettingsIcon,
  SpacesIcon,
  TasksIcon,
  WhiteboardIcon,
  type IconProps,
} from '@/components/ui/icons';
import { cn, STATUS_LABELS } from '@/lib/utils';

interface Item {
  id: string;
  group: string;
  label: string;
  hint?: string;
  Icon?: React.ComponentType<IconProps>;
  avatar?: React.ReactNode;
  run: () => void;
}

export function CommandPalette() {
  const { commandPaletteOpen, setCommandPalette } = useUi();
  const navigate = useNavigate();
  const openDm = useOpenDm();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { data, isFetching } = useSearch(query);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [commandPaletteOpen]);

  const items = useMemo<Item[]>(() => {
    const close = () => setCommandPalette(false);
    const go = (path: string) => () => {
      close();
      navigate(path);
    };

    if (!query.trim()) {
      return [
        { id: 'n1', group: 'Go to', label: 'Spaces', Icon: SpacesIcon, run: go('/spaces') },
        { id: 'n2', group: 'Go to', label: 'My tasks', Icon: TasksIcon, run: go('/my-tasks') },
        { id: 'n3', group: 'Go to', label: 'Chat', Icon: ChatIcon, run: go('/chat') },
        { id: 'n4', group: 'Go to', label: 'Calendar', Icon: MeetingsIcon, run: go('/calendar') },
        { id: 'n4b', group: 'Go to', label: 'Meetings', Icon: MeetingsIcon, run: go('/meetings') },
        {
          id: 'n5',
          group: 'Go to',
          label: 'Whiteboards',
          Icon: WhiteboardIcon,
          run: go('/whiteboards'),
        },
        { id: 'n6', group: 'Go to', label: 'Files', Icon: FilesIcon, run: go('/files') },
        { id: 'n7', group: 'Go to', label: 'People', Icon: PeopleIcon, run: go('/people') },
        { id: 'n8', group: 'Go to', label: 'Settings', Icon: SettingsIcon, run: go('/settings') },
      ];
    }

    if (!data) return [];

    return [
      ...data.tasks.map<Item>((task) => ({
        id: `task-${task.id}`,
        group: 'Tasks',
        label: task.title,
        hint: STATUS_LABELS[task.status],
        Icon: TasksIcon,
        run: go(`/projects/${task.projectId}?task=${task.id}`),
      })),
      ...data.projects.map<Item>((project) => ({
        id: `project-${project.id}`,
        group: 'Projects',
        label: project.name,
        hint: project.key,
        Icon: ProjectsIcon,
        run: go(`/projects/${project.id}`),
      })),
      ...data.channels.map<Item>((channel) => ({
        id: `channel-${channel.id}`,
        group: 'Channels',
        label: `#${channel.name}`,
        Icon: ChannelIcon,
        run: go(`/chat/${channel.id}`),
      })),
      ...data.people.map<Item>((person) => ({
        id: `person-${person.id}`,
        group: 'People',
        label: person.name,
        hint: person.title,
        avatar: <Avatar user={person} size="xs" />,
        run: async () => {
          close();
          const channel = await openDm.mutateAsync([person.id]);
          navigate(`/chat/${channel.id}`);
        },
      })),
      ...data.messages.map<Item>((message) => ({
        id: `message-${message.id}`,
        group: 'Messages',
        label: message.body.slice(0, 80),
        hint: message.channelName ? `#${message.channelName}` : undefined,
        Icon: ChatIcon,
        run: go(`/chat/${message.channelId}`),
      })),
      ...data.files.map<Item>((file) => ({
        id: `file-${file.id}`,
        group: 'Files',
        label: file.name,
        Icon: FilesIcon,
        run: go('/files'),
      })),
    ];
  }, [query, data, navigate, setCommandPalette, openDm]);

  useEffect(() => setCursor(0), [items.length]);

  /* Keep the highlighted row in view when arrowing past the fold. */
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!commandPaletteOpen) return null;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      items[cursor]?.run();
    } else if (e.key === 'Escape') {
      setCommandPalette(false);
    }
  }

  let lastGroup = '';

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-3 pt-[12vh]">
      <div
        className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-sm"
        onClick={() => setCommandPalette(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="relative w-full max-w-xl animate-slide-up overflow-hidden rounded-2xl border border-surface-3 bg-surface-1 shadow-overlay"
      >
        <div className="flex items-center gap-3 border-b border-surface-3 px-4">
          <span className="shrink-0 text-slate-400">
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search tasks, people, channels and files"
            placeholder="Search tasks, people, channels, files…"
            className="flex-1 bg-transparent py-4 text-sm text-slate-100 outline-none placeholder:text-slate-500"
          />
          {isFetching && <Spinner />}
        </div>

        <div ref={listRef} className="max-h-80 overflow-y-auto overscroll-contain py-1.5">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">
              {query ? (
                <>
                  Nothing matched <span className="text-slate-200">“{query}”</span>.
                  <br />
                  <span className="text-slate-500">Try a shorter word, or a person’s name.</span>
                </>
              ) : (
                'Start typing to search.'
              )}
            </p>
          ) : (
            items.map((item, index) => {
              const showGroup = item.group !== lastGroup;
              lastGroup = item.group;
              const active = index === cursor;
              return (
                <div key={item.id}>
                  {showGroup && (
                    <p className="px-4 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {item.group}
                    </p>
                  )}
                  <button
                    type="button"
                    data-active={active}
                    onMouseEnter={() => setCursor(index)}
                    onClick={item.run}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                      active ? 'bg-brand-600/20 text-ink' : 'text-slate-300',
                    )}
                  >
                    <span className="grid w-5 shrink-0 place-items-center text-slate-400">
                      {item.avatar ?? (item.Icon ? <item.Icon /> : null)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.hint && (
                      <span className="shrink-0 text-xs text-slate-500">{item.hint}</span>
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <footer className="flex gap-4 border-t border-surface-3 px-4 py-2 text-[11px] text-slate-500">
          <span>
            <kbd className="rounded border border-surface-4 px-1">↑</kbd>
            <kbd className="ml-0.5 rounded border border-surface-4 px-1">↓</kbd> navigate
          </span>
          <span>
            <kbd className="rounded border border-surface-4 px-1">↵</kbd> open
          </span>
          <span>
            <kbd className="rounded border border-surface-4 px-1">esc</kbd> close
          </span>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
